import { useMemo, useState } from "react";

import { MAX_PAGE_SIZE } from "@/core/api/pagination";
import type { SearchableSelectOption } from "@/core/components/ui/searchable-select";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Checkbox } from "@/core/components/ui/checkbox";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { usePatchMerchantPermissions } from "@/features/merchant/api/use-patch-merchant-permissions";
import { useSetMerchantKycStatus } from "@/features/merchant/api/use-set-merchant-kyc-status";
import { useSetSettlementWallet } from "@/features/merchant/api/use-set-settlement-wallet";
import type { MerchantDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  MERCHANT_KYC_STATUSES,
  MERCHANT_PERMISSION_CODES,
  type MerchantKycStatus,
} from "@/features/shared/api/enums";
import { useWallets } from "@/features/wallet/api/use-wallets";

/**
 * Bloc d'actions d'administration d'un marchand (page détail) : statut KYC,
 * portefeuille de reversement, permissions granulaires. Chaque action est
 * réservée aux rôles back-office (à envelopper via `RequireRole` à
 * l'appelant si besoin, cf. AGENTS.md §RBAC).
 */
export function MerchantAdminActions({ merchant }: { merchant: MerchantDto }) {
  const { t: tm } = useMerchantTranslation();

  const setKycStatus = useSetMerchantKycStatus();
  const setSettlementWallet = useSetSettlementWallet();
  const patchPermissions = usePatchMerchantPermissions();

  const [kycStatus, setKycStatusValue] = useState<MerchantKycStatus>(
    merchant.kycStatus ?? "NONE"
  );
  const [walletId, setWalletId] = useState<string>(
    merchant.settlementWalletId ?? ""
  );
  const { data: walletsPage, isLoading: walletsLoading } = useWallets({
    filter: { ownerPartyId: merchant.ownerPartyId },
    page: 1,
    pageSize: MAX_PAGE_SIZE,
  });
  const walletOptions = useMemo<SearchableSelectOption[]>(() => {
    const seen = new Set<string>();
    const options: SearchableSelectOption[] = [];
    for (const w of walletsPage?.items ?? []) {
      if (!w.id || seen.has(w.id)) continue;
      seen.add(w.id);
      const label = [w.walletNumber, w.ownerName, w.ownerType, w.currency]
        .map((part) => part?.trim?.() ?? part)
        .filter((part): part is string => Boolean(part))
        .join(" · ");
      options.push({
        value: w.id,
        label: label || w.id,
        description: label && label !== w.id ? w.id : undefined,
      });
    }
    return options;
  }, [walletsPage?.items]);
  const [updatedByKeycloakId, setUpdatedByKeycloakId] = useState<string>("");
  const [permissions, setPermissions] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(MERCHANT_PERMISSION_CODES.map((code) => [code, false]))
  );
  const [preview, setPreview] = useState<
    "kyc" | "settlement" | "permissions" | null
  >(null);

  if (!merchant.id) return null;

  const merchantId = merchant.id;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {/* Statut KYC */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {tm("merchant.merchants.kycStatus.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>{tm("merchant.merchants.kycStatus.value")}</Label>
            <Select
              value={kycStatus}
              onValueChange={(v) => setKycStatusValue(v as MerchantKycStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MERCHANT_KYC_STATUSES.map((v) => (
                  <SelectItem key={v} value={v} className="notranslate">
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            disabled={setKycStatus.isPending}
            onClick={() => setPreview("kyc")}
          >
            {setKycStatus.isPending ? <ButtonSpinner /> : null}
            {tm("merchant.merchants.kycStatus.submit")}
          </Button>
        </CardContent>
      </Card>

      {/* Settlement wallet */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {tm("merchant.merchants.settlementWallet.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="settlement-wallet-id">
              {tm("merchant.merchants.settlementWallet.walletId")}
            </Label>
            <EntitySearchableSelect
              id="settlement-wallet-id"
              options={walletOptions}
              value={walletId}
              onValueChange={setWalletId}
              placeholder={tm(
                "merchant.merchants.settlementWallet.walletIdPlaceholder"
              )}
              isLoading={walletsLoading}
            />
          </div>
          <Button
            type="button"
            disabled={setSettlementWallet.isPending || !walletId.trim()}
            onClick={() => setPreview("settlement")}
          >
            {setSettlementWallet.isPending ? <ButtonSpinner /> : null}
            {tm("merchant.merchants.settlementWallet.submit")}
          </Button>
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card className="md:col-span-2 xl:col-span-1">
        <CardHeader>
          <CardTitle className="text-base">
            {tm("merchant.merchants.permissions.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="updated-by-keycloak">
              {tm("merchant.merchants.permissions.updatedByKeycloakId")}
            </Label>
            <Input
              id="updated-by-keycloak"
              className="notranslate"
              placeholder={tm(
                "merchant.merchants.permissions.updatedByKeycloakIdPlaceholder"
              )}
              value={updatedByKeycloakId}
              onChange={(e) => setUpdatedByKeycloakId(e.target.value)}
            />
          </div>
          <ul className="space-y-2">
            {MERCHANT_PERMISSION_CODES.map((code) => (
              <li key={code} className="flex items-center gap-2">
                <Checkbox
                  id={`perm-${code}`}
                  checked={permissions[code] ?? false}
                  onCheckedChange={(checked) =>
                    setPermissions((prev) => ({
                      ...prev,
                      [code]: checked === true,
                    }))
                  }
                />
                <Label htmlFor={`perm-${code}`} className="notranslate text-xs">
                  {code}
                </Label>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            disabled={patchPermissions.isPending}
            onClick={() => setPreview("permissions")}
          >
            {patchPermissions.isPending ? <ButtonSpinner /> : null}
            {tm("merchant.merchants.permissions.submit")}
          </Button>
        </CardContent>
      </Card>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={
          preview === "kyc"
            ? { merchantId, kycStatus }
            : preview === "settlement"
              ? { merchantId, walletId: walletId.trim() }
              : {
                  merchantId,
                  updatedByKeycloakId: updatedByKeycloakId.trim() || undefined,
                  permissions: MERCHANT_PERMISSION_CODES.map((code) => ({
                    permissionCode: code,
                    isGranted: permissions[code] ?? false,
                  })),
                }
        }
        title={tm("common.mutationPreview.updateTitle")}
        description={tm("common.mutationPreview.updateDescription")}
        t={tm}
        pending={
          setKycStatus.isPending ||
          setSettlementWallet.isPending ||
          patchPermissions.isPending
        }
        onConfirm={() => {
          if (preview === "kyc") {
            setKycStatus.mutate(
              { merchantId, kycStatus },
              {
                onSuccess: () => {
                  notifSilence(
                    tm("merchant.merchants.feedback.kycStatusUpdated")
                  );
                  setPreview(null);
                },
                onError: (error) => notifSilenceError(error.message),
              }
            );
            return;
          }
          if (preview === "settlement") {
            setSettlementWallet.mutate(
              { merchantId, walletId: walletId.trim() },
              {
                onSuccess: () => {
                  notifSilence(
                    tm("merchant.merchants.feedback.settlementWalletLinked")
                  );
                  setPreview(null);
                },
                onError: (error) => notifSilenceError(error.message),
              }
            );
            return;
          }
          patchPermissions.mutate(
            {
              merchantId,
              updatedByKeycloakId: updatedByKeycloakId.trim() || undefined,
              body: {
                permissions: MERCHANT_PERMISSION_CODES.map((code) => ({
                  permissionCode: code,
                  isGranted: permissions[code] ?? false,
                })),
              },
            },
            {
              onSuccess: () => {
                notifSilence(
                  tm("merchant.merchants.feedback.permissionsUpdated")
                );
                setPreview(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </div>
  );
}
